import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { 
  FileText, ChevronDown, ChevronRight, Calendar, BookOpen, 
  Loader2, List, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../context/PreferencesContext';
import { useModuleAccess } from '../hooks/useModuleAccess';
import ModuleAccessOverlay from '../components/common/ModuleAccessOverlay';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WrittenQuestionBankPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { preferences } = usePreferences();
  
  // Module access check
  const { getWrittenStatus, loading: moduleLoading } = useModuleAccess();
  const userGrade = preferences?.grade;
  const writtenStatus = userGrade ? getWrittenStatus(userGrade) : 'active';
  
  const [papers, setPapers] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFunction, setSelectedFunction] = useState('all');
  
  // Year/Month filter states
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  
  // Year expansion states - current year expanded by default
  const currentYear = new Date().getFullYear();
  const [expandedYears, setExpandedYears] = useState({ [currentYear]: true });
  
  // Function selection modal for "View All Questions"
  const [showFunctionModal, setShowFunctionModal] = useState(false);
  const [selectedFunctionForAll, setSelectedFunctionForAll] = useState('');

  useEffect(() => {
    // Gate data fetch on BOTH grade and MMD being set — same rule as the
    // Oral Question Bank. Selecting grade + MMD is a mandatory one-time
    // setup for the whole app: it drives module status, surveyor filters,
    // hot-topic analytics, and future MMD-aware Written features. We fire
    // no API calls until both are set, then show a full-page prompt (below)
    // that sends the user to /grade.
    if (userGrade && preferences?.mmd_location) {
      fetchData();
    }
  }, [userGrade, preferences?.mmd_location]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [papersRes, filtersRes] = await Promise.all([
        axios.get(`${API}/written/papers/public`, { params: { grade: userGrade } }),
        axios.get(`${API}/written/filters/public`)
      ]);
      
      setPapers(papersRes.data.papers || []);
      
      // Filter functions for user's grade
      const gradeFunctions = (filtersRes.data.functions || []).filter(
        f => f.grade === userGrade
      );
      setFunctions(gradeFunctions);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load written papers');
    } finally {
      setLoading(false);
    }
  };

  // Get available years from papers
  const availableYears = useMemo(() => {
    const yearCounts = {};
    papers.forEach(paper => {
      const match = paper.exam_month?.match(/\d{4}/);
      const year = match ? match[0] : null;
      if (year) {
        yearCounts[year] = (yearCounts[year] || 0) + 1;
      }
    });
    return Object.entries(yearCounts)
      .map(([year, count]) => ({ year, count }))
      .sort((a, b) => b.year - a.year);
  }, [papers]);

  // Get available months for selected year
  const availableMonthsForYear = useMemo(() => {
    if (selectedYear === 'all') return [];
    
    const monthCounts = {};
    const monthOrder = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    
    papers.forEach(paper => {
      if (paper.exam_month?.includes(selectedYear)) {
        const monthMatch = monthOrder.find(m => paper.exam_month?.includes(m));
        if (monthMatch) {
          monthCounts[monthMatch] = (monthCounts[monthMatch] || 0) + 1;
        }
      }
    });
    
    return monthOrder
      .filter(m => monthCounts[m])
      .map(month => ({ month, count: monthCounts[month] }));
  }, [papers, selectedYear]);

  // Filter papers by function and year/month
  const filteredPapers = useMemo(() => {
    let result = papers;
    
    // Function filter
    if (selectedFunction !== 'all') {
      result = result.filter(p => p.function_id === selectedFunction);
    }
    
    // Year filter
    if (selectedYear !== 'all') {
      result = result.filter(p => p.exam_month?.includes(selectedYear));
    }
    
    // Month filter
    if (selectedMonth !== 'all') {
      result = result.filter(p => p.exam_month?.includes(selectedMonth));
    }
    
    return result;
  }, [papers, selectedFunction, selectedYear, selectedMonth]);

  // Group papers by year
  const papersByYear = useMemo(() => {
    const grouped = {};
    filteredPapers.forEach(paper => {
      const match = paper.exam_month?.match(/\d{4}/);
      const year = match ? parseInt(match[0]) : currentYear;
      if (!grouped[year]) grouped[year] = [];
      grouped[year].push(paper);
    });
    
    // Sort papers within each year by month (newest first)
    const monthOrder = ['December', 'November', 'October', 'September', 'August', 'July', 
                        'June', 'May', 'April', 'March', 'February', 'January'];
    Object.keys(grouped).forEach(year => {
      grouped[year].sort((a, b) => {
        const monthA = monthOrder.findIndex(m => a.exam_month?.includes(m));
        const monthB = monthOrder.findIndex(m => b.exam_month?.includes(m));
        if (monthA !== monthB) return monthA - monthB;
        return (a.paper_number || 0) - (b.paper_number || 0);
      });
    });
    
    return grouped;
  }, [filteredPapers, currentYear]);

  // Get sorted years (newest first)
  const sortedYears = useMemo(() => {
    return Object.keys(papersByYear).map(Number).sort((a, b) => b - a);
  }, [papersByYear]);

  // Toggle year expansion
  const toggleYear = (year) => {
    setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }));
  };

  // Get paper display name
  const getPaperDisplayName = (paper) => {
    if (paper.paper_number) {
      return `${paper.exam_month} - Paper ${paper.paper_number}`;
    }
    return paper.exam_month;
  };

  // Handle "View All Questions" click
  const handleViewAllClick = () => {
    if (functions.length === 1) {
      // Only one function, go directly
      navigate(`/written-bank/all?function=${functions[0].id}`);
    } else {
      setShowFunctionModal(true);
    }
  };

  // Handle function selection for "View All"
  const handleFunctionSelect = () => {
    if (selectedFunctionForAll) {
      navigate(`/written-bank/all?function=${selectedFunctionForAll}`);
      setShowFunctionModal(false);
    }
  };

  // No grade or MMD selected — prompt user to set both (mirrors the Oral
  // Question Bank gate exactly so the two flows feel identical).
  if (!userGrade || !preferences?.mmd_location) {
    return (
      <Layout>
        <ModuleAccessOverlay
          status="disabled"
          featureName="Written Question Bank"
          customMessage="Please select your grade and MMD location first to access the Written Question Bank."
          showBackButton={true}
          showHomeButton={false}
        />
        <div className="flex justify-center mt-4">
          <Button
            onClick={() => navigate('/grade')}
            className="bg-[#0A7EA4] hover:bg-[#0A7EA4]/90"
            data-testid="written-select-grade-mmd-btn"
          >
            Select Grade &amp; MMD
          </Button>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#14B8A6]" />
        </div>
      </Layout>
    );
  }

  // Module access enforcement - show overlay if written is not active
  if (!moduleLoading && writtenStatus && writtenStatus !== 'active') {
    return (
      <Layout>
        <ModuleAccessOverlay
          status={writtenStatus}
          featureName="Written Question Bank"
          description={
            writtenStatus === 'coming_soon'
              ? `The Written Question Bank for ${userGrade || 'your grade'} is coming soon! We're preparing this content and it will be available shortly.`
              : `The Written Question Bank is currently not available for ${userGrade || 'your grade'}.`
          }
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-transition">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Written Question Bank</h1>
            <p className="text-gray-600 mt-1">
              <Badge variant="static" className="bg-[#14B8A6] text-white mr-2">{userGrade}</Badge>
              {filteredPapers.length} {filteredPapers.length === 1 ? 'paper' : 'papers'} available
            </p>
          </div>
          <Button 
            onClick={handleViewAllClick}
            className="w-full md:w-auto bg-[#EC4899] hover:bg-[#EC4899]/90"
          >
            <List className="w-4 h-4 mr-2" /> View All Questions
          </Button>
        </div>

        {/* Filters Card */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="font-medium text-gray-700">Filters</span>
            </div>
            
            {/* Function Filter Pills */}
            {functions.length > 0 && (
              <div className="mb-4">
                <Label className="text-sm text-gray-600 mb-2 block">Function</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={selectedFunction === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedFunction('all')}
                    className={selectedFunction === 'all' ? 'bg-[#14B8A6] hover:bg-[#14B8A6]/90' : ''}
                  >
                    All Functions
                  </Button>
                  {functions.map(func => (
                    <Button
                      key={func.id}
                      variant={selectedFunction === func.id ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedFunction(func.id)}
                      className={`max-w-full h-auto min-h-9 py-1.5 whitespace-normal break-words text-left leading-snug ${selectedFunction === func.id ? 'bg-[#14B8A6] hover:bg-[#14B8A6]/90' : ''}`}
                    >
                      {func.display}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Year and Month Filter Dropdowns */}
            <div>
              <Label className="text-sm text-gray-600 mb-2 block">Period</Label>
              <div className="flex flex-wrap gap-3">
                {/* Year Dropdown */}
                <Select 
                  value={selectedYear} 
                  onValueChange={(value) => {
                    setSelectedYear(value);
                    setSelectedMonth('all'); // Reset month when year changes
                  }}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="All Years" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Years</SelectItem>
                    {availableYears.map(({ year, count }) => (
                      <SelectItem key={year} value={year}>
                        {year} ({count} {count === 1 ? 'paper' : 'papers'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Month Dropdown - Only enabled when year is selected */}
                <Select 
                  value={selectedMonth} 
                  onValueChange={setSelectedMonth}
                  disabled={selectedYear === 'all'}
                >
                  <SelectTrigger className={`w-[180px] ${selectedYear === 'all' ? 'opacity-50' : ''}`}>
                    <SelectValue placeholder={selectedYear === 'all' ? 'Select year first' : 'All Months'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Months</SelectItem>
                    {availableMonthsForYear.map(({ month, count }) => (
                      <SelectItem key={month} value={month}>
                        {month} ({count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Papers List - Year-wise Collapsible */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#14B8A6]" />
              Written Papers
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredPapers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No written papers found for your grade</p>
                {selectedFunction !== 'all' && (
                  <p className="text-sm mt-1">Try selecting &quot;All Functions&quot;</p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {sortedYears.map(year => (
                  <Collapsible 
                    key={year} 
                    open={expandedYears[year]} 
                    onOpenChange={() => toggleYear(year)}
                  >
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-3">
                          {expandedYears[year] ? (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          )}
                          <Calendar className="w-5 h-5 text-[#14B8A6]" />
                          <span className="font-semibold text-lg">{year}</span>
                          <Badge variant="outline" className="ml-2">
                            {papersByYear[year].length} {papersByYear[year].length === 1 ? 'paper' : 'papers'}
                          </Badge>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4 px-1">
                        {papersByYear[year].map(paper => (
                          <div
                            key={paper.id}
                            onClick={() => navigate(`/written-bank/${paper.id}`)}
                            className="p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md hover:border-[#14B8A6] bg-white"
                          >
                            {/* Function Badge */}
                            <div className="flex justify-between items-start mb-2">
                              <Badge variant="static" className="bg-[#14B8A6]/10 text-[#14B8A6] text-xs">
                                {paper.function_display}
                              </Badge>
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${paper.status === 'published' 
                                  ? 'border-green-500 text-green-600' 
                                  : 'border-amber-500 text-amber-600'}`}
                              >
                                {paper.question_count} Q
                              </Badge>
                            </div>
                            
                            {/* Paper Title */}
                            <h3 className="font-semibold text-gray-900 mb-1">
                              {getPaperDisplayName(paper)}
                            </h3>
                            
                            {/* Function Name */}
                            <p className="text-sm text-gray-600">{paper.function_name}</p>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Function Selection Modal for "View All Questions" */}
        <Dialog open={showFunctionModal} onOpenChange={setShowFunctionModal}>
          <DialogContent className="max-w-md overflow-hidden">
            <DialogHeader>
              <DialogTitle>Select Function</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4 min-w-0">
              <p className="text-sm text-gray-600">
                Choose a function to view all its questions across all papers.
              </p>
              <Select value={selectedFunctionForAll} onValueChange={setSelectedFunctionForAll}>
                <SelectTrigger className="min-w-0 overflow-hidden [&>span]:min-w-0 [&>span]:flex-1 [&>span]:truncate [&>span]:text-left">
                  <SelectValue placeholder="Select a function..." />
                </SelectTrigger>
                <SelectContent>
                  {functions.map(func => (
                    <SelectItem key={func.id} value={func.id}>
                      {func.display}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowFunctionModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleFunctionSelect}
                  disabled={!selectedFunctionForAll}
                  className="flex-1 bg-[#EC4899] hover:bg-[#EC4899]/90"
                >
                  View Questions
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default WrittenQuestionBankPage;
